import { Link } from "react-router-dom";
import type { AccuracyReport, OutcomeCounts } from "../api/types";
import { Empty } from "./States";

const pct = (v: number | null) => (v === null ? "—" : `${(v * 100).toFixed(1)}%`);

/**
 * The five outcomes, in the order a reader should weigh them.
 *
 * `hallucinated` is last and flagged, not because it is least important but
 * because it is the one worth stopping on: a value the document does not
 * contain, produced anyway. On a KYC file that is fabricated evidence rather
 * than a mistake, and folding it into an average is how it stays invisible.
 */
const OUTCOMES: {
  key: keyof OutcomeCounts;
  label: string;
  hint: string;
  tone?: "good" | "warn" | "bad";
}[] = [
  { key: "correct", label: "Correct", hint: "the value matches the truth", tone: "good" },
  {
    key: "correct_absent",
    label: "Correctly empty",
    hint: "the document has no such value and the model reported none",
    tone: "good",
  },
  {
    key: "missed",
    label: "Missed",
    hint: "the value is there; the model reported nothing. Costs review time, puts nothing false on file",
    tone: "warn",
  },
  {
    key: "wrong",
    label: "Wrong",
    hint: "a value was produced and it is not the truth — usually a layout or prompt problem",
    tone: "warn",
  },
  {
    key: "hallucinated",
    label: "Invented",
    hint: "the document has no such value and the model produced one anyway",
    tone: "bad",
  },
];

/**
 * Measured accuracy for one agent, against documents a person has verified.
 *
 * Every other figure on this page is the model's own opinion or a policy
 * applied to it. This one compares extractions against a human reading of the
 * page, which is why it is the only number here that can be wrong in the
 * reader's favour and still be called accuracy.
 */
export function AgentAccuracy({
  report,
  documentType,
}: {
  report: AccuracyReport;
  documentType: string;
}) {
  if (report.scored === 0) {
    return (
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Measured accuracy</span>
        </div>
        <div className="card-body">
          <Empty
            title="Nothing has been verified for this agent yet"
            hint="Accuracy needs a person to read a document and record what it says. Open a low-confidence extraction and check its fields against the page."
          />
          <div className="form-actions">
            <span className="muted actions-note">
              Start with the extractions the model was least sure about.
            </span>
            <Link
              className="btn btn-primary"
              to={`/documents?document_type=${encodeURIComponent(documentType)}&confidence_band=0-50`}
            >
              Review lowest confidence
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const m = report.metrics;
  const fields = Object.entries(report.by_field_metrics).sort(
    (a, b) => (a[1].accuracy ?? 1) - (b[1].accuracy ?? 1),
  );

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">Measured accuracy</span>
        <span className="muted">
          {report.verified_documents} document(s), {report.scored} field(s) verified
        </span>
      </div>
      <div className="card-body">
        {report.note ? <div className="notice notice-warn">{report.note}</div> : null}

        <div className="acc-metrics">
          <div className="acc-metric">
            <span className="tile-label">Precision</span>
            <span className="acc-value">{pct(m.precision)}</span>
            <span className="tile-note">of the values it produced, how many were right</span>
          </div>
          <div className="acc-metric">
            <span className="tile-label">Recall</span>
            <span className="acc-value">{pct(m.recall)}</span>
            <span className="tile-note">of the values that exist, how many it got</span>
          </div>
          <div className="acc-metric flag">
            <span className="tile-label">Invention rate</span>
            <span
              className={`acc-value${(m.hallucination_rate ?? 0) > 0 ? " bad" : ""}`}
            >
              {pct(m.hallucination_rate)}
            </span>
            <span className="tile-note">
              of the fields a document does not have, how often one was invented
            </span>
          </div>
        </div>

        <div className="outcome-bar" role="img" aria-label="Outcome distribution">
          {OUTCOMES.map((o) => {
            const n = report.totals[o.key] ?? 0;
            if (!n) return null;
            return (
              <span
                key={o.key}
                className={`outcome-seg ${o.tone ?? ""}`}
                style={{ flexGrow: n }}
                title={`${o.label}: ${n} — ${o.hint}`}
              />
            );
          })}
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Outcome</th>
                <th scope="col" className="num">
                  Count
                </th>
                <th scope="col">What it means</th>
              </tr>
            </thead>
            <tbody>
              {OUTCOMES.map((o) => (
                <tr key={o.key}>
                  <td className="nowrap">
                    <span className={`outcome-dot ${o.tone ?? ""}`} />
                    {o.label}
                  </td>
                  <td className="num">
                    <span
                      className={
                        o.key === "hallucinated" && (report.totals[o.key] ?? 0) > 0
                          ? "req-bad"
                          : undefined
                      }
                    >
                      {report.totals[o.key] ?? 0}
                    </span>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {o.hint}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {fields.length ? (
          <>
            <h3 className="section-title" style={{ marginTop: 26 }}>
              By field — weakest first
            </h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col" className="num">Checked</th>
                    <th scope="col" className="num">Accuracy</th>
                    <th scope="col" className="num">Precision</th>
                    <th scope="col" className="num">Recall</th>
                    <th scope="col" className="num">Invented</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map(([field, fm]) => (
                    <tr key={field}>
                      <td className="mono">{field}</td>
                      <td className="num muted">{fm.n}</td>
                      <td className="num">
                        <span
                          className={
                            fm.accuracy === null
                              ? undefined
                              : fm.accuracy >= 0.95
                                ? "req-ok"
                                : fm.accuracy >= 0.7
                                  ? "req-warn"
                                  : "req-bad"
                          }
                        >
                          {pct(fm.accuracy)}
                        </span>
                      </td>
                      <td className="num muted">{pct(fm.precision)}</td>
                      <td className="num muted">{pct(fm.recall)}</td>
                      <td className="num">
                        <span
                          className={
                            (report.by_field[field]?.hallucinated ?? 0) > 0
                              ? "req-bad"
                              : "muted"
                          }
                        >
                          {report.by_field[field]?.hallucinated ?? 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {report.failures.length ? (
          <>
            <h3 className="section-title" style={{ marginTop: 26 }}>
              Where it went wrong
              {report.failure_count > report.failures.length ? (
                <span className="muted" style={{ fontWeight: 400 }}>
                  {" "}
                  — showing {report.failures.length} of {report.failure_count}
                </span>
              ) : null}
            </h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Outcome</th>
                    <th scope="col">Should be</th>
                    <th scope="col">Model read</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  {report.failures.map((f, i) => (
                    <tr key={`${f.document_id}-${f.field}-${i}`}>
                      <td className="mono">{f.field}</td>
                      <td className="nowrap">
                        <span
                          className={`pill ${f.outcome === "hallucinated" ? "pill-critical" : "pill-warning"}`}
                        >
                          {f.outcome === "hallucinated" ? "invented" : f.outcome}
                        </span>
                      </td>
                      <td className="value-cell">{f.expected}</td>
                      <td className="value-cell">{f.actual}</td>
                      <td className="nowrap">
                        <Link to={`/documents/${f.document_id}?tab=verify`}>open</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {report.orphaned_documents ? (
          <p className="muted" style={{ fontSize: 12.5, marginTop: 14 }}>
            {report.orphaned_documents} verified document(s) no longer exist and were
            excluded — scoring them either way would move the number for a reason
            unrelated to model quality.
          </p>
        ) : null}
      </div>
    </div>
  );
}
